import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useToast } from "../../hooks/useToast";
import { Link } from "react-router-dom";
import csmGrayTextLogin from "../../assets/images/csmGrayTextLogin.png";
import { emailLinkSent } from "../../services/firebase";
import { logUserActionBackground, USER_ACTION_EVENTS } from "../../services/userActionLog";

export default function VerifyEmailSent() {
  const auth = getAuth();
  const storedEmail =
    typeof window !== "undefined"
      ? (localStorage.getItem("pendingRegistrationEmail") || "").trim()
      : "";
  const email = auth?.currentUser?.email || storedEmail || undefined;
  const { toast } = useToast();
  const [timer, setTimer] = useState(0);
  const [resendCount, setResendCount] = useState(1);
  const maxResendAttempts = 3;

  useEffect(() => {
    const checkVerificationAndRedirect = async () => {
      const user = auth.currentUser;
      if (!user) return false;

      try {
        await user.reload();
        const refreshedUser = auth.currentUser;
        if (!refreshedUser?.emailVerified) return false;

        localStorage.removeItem("verify");
        localStorage.removeItem("pendingRegistrationEmail");
        window.location.replace("/thank-you");
        return true;
      } catch (error) {
        console.error("Error checking email verification:", error);
        // Continue polling even if there's a temporary error
        return false;
      }
    };

    const interval = setInterval(async () => {
      const redirected = await checkVerificationAndRedirect();
      if (redirected) clearInterval(interval);
    }, 3000);

    const handleTabActive = () => {
      if (document.visibilityState === "visible") {
        checkVerificationAndRedirect();
      }
    };

    window.addEventListener("focus", handleTabActive);
    document.addEventListener("visibilitychange", handleTabActive);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleTabActive);
      document.removeEventListener("visibilitychange", handleTabActive);
    };
  }, []);

  useEffect(() => {
    const hasVerifyFlag = localStorage.getItem("verify");
    if (hasVerifyFlag === "true") {
      localStorage.removeItem("verify");
      setTimer(60);
      logUserActionBackground({
        userId: auth?.currentUser?.uid || "landed-on-verify-sent",
        eventName: USER_ACTION_EVENTS.LANDED_ON_VERIFY_EMAIL_SENT,
        email: email || "unknown",
      });
    }
  }, []);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
    if (!auth?.currentUser || resendCount >= maxResendAttempts || timer > 0) return;

    try {
      // await sendEmailVerification(auth.currentUser, {
      //   url: `https://${DOMAINURL}/firebase-action-handler?email=${email}`,
      //   handleCodeInApp: true,
      // });

      await emailLinkSent(email, null);
      
      toast?.({
        title: "Verification Sent",
        description: `Verification email sent to ${email}`,
        variant: "default",
      });

      setResendCount((prev) => prev + 1);
      setTimer(60);
    } catch (error) {
      console.error("Error resending verification:", error);
      toast?.({
        title: "Error",
        description:
          error.code === "auth/too-many-requests"
            ? "You’ve tried too many times. Please wait before trying again."
            : "Failed to send verification email.",
        variant: "destructive",
      });
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "#f0fdf4",
        padding: "20px",
      }}
    > 
    <img
        src={csmGrayTextLogin}
        alt="ProTeams Logo"
        style={{
          height: "80px",
          marginBottom: "30px",
        }}
      />
      <div
        data-testid="verify-email-sent-page"
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "40px 30px",
          boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
          textAlign: "center",
          maxWidth: "500px",
          width: "100%",
        }}
      >
        <div style={{ marginBottom: "20px", color: "#16a34a" }}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 50 50"
            style={{ animation: "spin 1s linear infinite" }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#16a34a"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
              transform="rotate(-90 25 25)"
            />
          </svg>
        </div>

        <h2 style={{ color: "#065f46", marginBottom: "10px", fontSize: "20px", fontWeight: "500" }}>
          Verify Your Email
        </h2>

        {email && (
          <p style={{ color: "#16a34a", fontWeight: 500, marginBottom: "10px" }}>
            Sent to: <span style={{ fontWeight: "600" }}>{email}</span>
          </p>
        )}

        <p style={{ color: "#065f46", fontSize: "16px", lineHeight: "22px" }}>
          Please check your inbox and click on the verification link we sent you.
          Once verified, you will be able to access your dashboard.
        </p>

        <button
          onClick={handleResend}
          disabled={timer > 0 || resendCount >= maxResendAttempts}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            backgroundColor:
              timer > 0 || resendCount >= maxResendAttempts ? "#ccc" : "#16a34a",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor:
              timer > 0 || resendCount >= maxResendAttempts
                ? "not-allowed"
                : "pointer",
            fontWeight: "500",
            fontSize: "15px",
          }}
        >
          {resendCount >= maxResendAttempts
            ? "Resend Limit Reached"
            : timer > 0
            ? `Resend in ${timer}s`
            : "Resend Verification Email"}
        </button>

        <div className="d-flex flex-justify-center mt-20">
          <Link to={"/login"} style={{color:"#166534", fontSize:"16px", fontWeight:"500", textDecoration:"underline"}}>
              Go to login
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
}
